import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { MainLayout } from './components/Layout/MainLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ScenarioCarousel } from './components/ScenarioCarousel/ScenarioCarousel';
import { AddScenarioButton } from './components/ScenarioCarousel/AddScenarioButton';
import { ParameterControls } from './components/ParameterControls/ParameterControls';
import { SliderInput } from './components/ParameterControls/SliderInput';
import { NetWorthChart } from './components/Chart/NetWorthChart';
import { CheckoutSuccess } from './components/CheckoutSuccess';
import { ProfilePage } from './components/Profile/ProfilePage';
import { PrivacyPolicy } from './components/Legal/PrivacyPolicy';
import { TermsOfService } from './components/Legal/TermsOfService';
// import { MetricsSummary } from './components/Chart/MetricsSummary';
import { useScenarios } from './hooks/useScenarios';
import { useDefaults, useAnalysis, useBaselineAnalysis } from './hooks/useAnalysis';
import { useDebounce } from './hooks/useDebounce';
import { Scenario, ScenarioType, ParameterConfig } from './types/app';
import { CalculatorParams, AnalysisResponse } from './types/api';
import { dlog, debugEnabled, safeJson } from './utils/debug';
import { SaveButton } from './components/SavedAnalyses/SaveButton';
import { SaveAnalysisModal } from './components/SavedAnalyses/SaveAnalysisModal';
import { SavedAnalysesList } from './components/SavedAnalyses/SavedAnalysesList';
import { TutorialModal } from './components/Tutorial/TutorialModal';
import { UpgradePrompt } from './components/Subscription/UpgradePrompt';
import { useAuth } from './hooks/useAuth';
import { MAX_FREE_SCENARIOS, MAX_PREMIUM_SCENARIOS } from './utils/constants';

// Create optimized parameters for API calls - always use actual scenario parameters
const createOptimizedParams = (scenario: Scenario, timeHorizonYears: number): CalculatorParams => {
  const baseParams = scenario.parameters;

  // Always include analysis settings with updated time horizon
  const analysisSettings = {
    ...baseParams.analysis_settings,
    time_horizon_years: timeHorizonYears,
  };

  // ✅ ALWAYS use the actual scenario parameters, regardless of scenario type
  // The backend will use scenario_type to determine analysis logic
  const optimized = {
    property_details: baseParams.property_details,
    loan_details: baseParams.loan_details,
    transaction_costs: baseParams.transaction_costs,
    market_assumptions: baseParams.market_assumptions,
    analysis_settings: analysisSettings,
    renting_params: {
      ...baseParams.renting_params,
      rental_strategy: {
        ...baseParams.renting_params?.rental_strategy,
        duration_years: timeHorizonYears,
      },
    },
    buying_params: {
      ...baseParams.buying_params,
      rental_strategy: {
        ...baseParams.buying_params?.rental_strategy,
        duration_years: timeHorizonYears,
      },
    },
    scenario_type: scenario.scenarioType,
  };

  if (debugEnabled) {
    dlog('[createOptimizedParams]', {
      scenarioId: scenario.id,
      scenarioType: scenario.scenarioType,
      title: scenario.title,
      homePrice: optimized.property_details?.home_price,
      rent: optimized.renting_params?.current_rent_living_alone,
      timeHorizon: optimized.analysis_settings?.time_horizon_years,
    });
  }

  return optimized;
};

// Build renting params for a comparison pairing so renting NW reflects the paired buying scenario
// const createComparisonParams = (
//   rentingScenario: Scenario,
//   buyingScenario: Scenario,
//   timeHorizonYears: number
// ): CalculatorParams => {
//   const rent = rentingScenario.parameters;
//   const buy = buyingScenario.parameters;
//   return {
//     // Use buy-side home/loan/transaction costs to define the alternative baseline
//     property_details: buy.property_details,
//     loan_details: buy.loan_details,
//     transaction_costs: buy.transaction_costs,
//     // Use renting-side market assumptions and renting details
//     market_assumptions: rent.market_assumptions,
//     renting_params: rent.renting_params,
//     // Include buy-side strategy for consistency
//     buying_params: buy.buying_params,
//     analysis_settings: {
//       ...rent.analysis_settings,
//       time_horizon_years: timeHorizonYears,
//     },
//   };
// };

// Helper to generate a default analysis title like: "Analysis October 19, 13:47"
function generateDefaultAnalysisTitle(date: Date = new Date()): string {
  const month = date.toLocaleString('en-US', { month: 'long' });
  const day = date.getDate();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `Analysis ${month} ${day}, ${hours}:${minutes}`;
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: false,
    },
  },
});

const AppContent: React.FC = () => {
  const [analysisTitle, setAnalysisTitle] = useState<string>(generateDefaultAnalysisTitle());
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isSavedAnalysesOpen, setIsSavedAnalysesOpen] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { user, loading: authLoading, subscriptionStatus } = useAuth();
  const [hasDismissedWelcomeGuide, setHasDismissedWelcomeGuide] = useState(false);

  // Configuration for the analysis period slider
  const analysisPeriodConfig: ParameterConfig = {
    key: 'analysis_period',
    label: 'Analysis Period',
    min: 1,
    max: 30,
    step: 1,
    formatValue: (value: number) => `${value} years`,
  };
  const [tutorialModalState, setTutorialModalState] = useState<{
    isOpen: boolean;
    defaultTab: 'welcome' | 'overview' | 'calculations';
    source: 'welcome' | 'manual';
  }>({
    isOpen: false,
    defaultTab: 'overview',
    source: 'manual',
  });
  const [hasViewedParameterControls, setHasViewedParameterControls] = useState(false);
  const [isScenarioUpgradePromptOpen, setIsScenarioUpgradePromptOpen] = useState(false);
  const { data: defaultParams, isLoading: defaultsLoading } = useDefaults();

  const hasPremiumSubscription = Boolean(
    subscriptionStatus?.has_active_subscription ||
    subscriptionStatus?.subscription_status === 'active' ||
    user?.subscription_status === 'active'
  );
  const maxScenarios = hasPremiumSubscription ? MAX_PREMIUM_SCENARIOS : MAX_FREE_SCENARIOS;

  const shouldShowWelcomeGuide = !authLoading && !defaultsLoading && !user && !hasDismissedWelcomeGuide;
  const analysisMutation = useAnalysis();
  const baselineAnalysisMutation = useBaselineAnalysis();

  useEffect(() => {
    if (shouldShowWelcomeGuide) {
      setTutorialModalState({
        isOpen: true,
        defaultTab: 'welcome',
        source: 'welcome',
      });
    }
  }, [shouldShowWelcomeGuide]);

  // Check for checkout success in URL
  const urlParams = new URLSearchParams(window.location.search);
  const isCheckoutSuccess = urlParams.get('checkout') === 'success';
  const isPortalSuccess = urlParams.get('portal') === 'success';
  
  // Check if we're on the profile page
  const isProfilePage = window.location.pathname === '/profile';
  const isPrivacyPage = window.location.pathname === '/privacy';
  const isTermsPage = window.location.pathname === '/terms';

  // Normalize portal return: if Stripe portal redirects to root, send to /profile
  useEffect(() => {
    if (isPortalSuccess && !isProfilePage) {
      window.location.replace('/profile?portal=success');
    }
  }, [isPortalSuccess, isProfilePage]);

  const {
    scenarios,
    activeScenario,
    activeScenarioId,
    timeHorizonYears,
    setActiveScenarioId,
    setTimeHorizonYears,
    addScenario,
    removeScenario,
    updateScenarioTitle,
    updateScenarioLink,
    updateScenarioParameters,
    updateScenarioResult,
    setScenarioAnalyzing,
    toggleScenarioVisibility,
    toggleBaseline,
    canAddScenario,
  } = useScenarios(defaultParams, maxScenarios);

  const scenarioCount = scenarios.length;
  const parameterControlsRef = useRef<HTMLDivElement | null>(null);
  const shouldPulseParameterControls = scenarioCount >= 2 && !hasViewedParameterControls;

  const handleOpenTutorialModal = useCallback(() => {
    setTutorialModalState((prev) => ({
      isOpen: true,
      defaultTab: 'overview',
      source: prev.isOpen ? prev.source : 'manual',
    }));
  }, []);

  const handleCloseTutorialModal = useCallback(() => {
    setTutorialModalState((prev) => ({ ...prev, isOpen: false }));
    if (tutorialModalState.source === 'welcome') {
      setHasDismissedWelcomeGuide(true);
    }
  }, [tutorialModalState.source]);

  const handleAddScenario = useCallback((scenarioType: ScenarioType) => {
    if (scenarioCount >= maxScenarios) {
      if (!hasPremiumSubscription) {
        setIsScenarioUpgradePromptOpen(true);
      }
      return;
    }

    addScenario(scenarioType);
  }, [scenarioCount, maxScenarios, hasPremiumSubscription, addScenario]);

  // Expose scenarios globally so header can harvest titles for saving
  useEffect(() => {
    (window as any).__SCENARIOS__ = scenarios;
  }, [scenarios]);

  useEffect(() => {
    if (!shouldPulseParameterControls) return;
    const target = parameterControlsRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.5) {
            setHasViewedParameterControls(true);
          }
        });
      },
      { threshold: [0.5] }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [shouldPulseParameterControls]);

  const baselineScenario = useMemo(
    () => scenarios.find(s => s.isBaseline) || null,
    [scenarios]
  );

  // Create a stable hash of parameters to detect real changes
  const getParametersHash = (scenario: any) => {
    if (!scenario?.parameters) return '';
    try {
      return JSON.stringify(scenario.parameters);
    } catch (error) {
      // Use deterministic fallback based on key scenario properties
      console.warn('JSON.stringify failed for scenario parameters, using deterministic fallback', error);
      const params = scenario.parameters || {};
      const key = [
        scenario.id,
        scenario.scenarioType,
        params.property_details?.home_price || 0,
        params.renting_params?.current_rent_living_alone || 0,
        params.buying_params?.rental_strategy?.monthly_income || 0,
        params.analysis_settings?.time_horizon_years || 0,
        params.market_assumptions?.investment_return_rate || 0,
      ].join('_');
      return key;
    }
  };

  // Create a hash that only changes when the active scenario's parameters actually change
  const activeScenarioParametersHash = useMemo(() => {
    if (!activeScenario?.parameters) return '';
    return getParametersHash(activeScenario);
  }, [
    activeScenario?.id,
    // Use a stable reference to parameters to avoid infinite re-renders
    activeScenario?.parameters
  ]);

  // Phase 1.1: Add comprehensive logging to track parameter hash changes
  useEffect(() => {
    console.log('[HASH_CHANGE]', {
      scenarioId: activeScenario?.id,
      scenarioTitle: activeScenario?.title,
      hash: activeScenarioParametersHash,
      timestamp: Date.now()
    });
  }, [activeScenarioParametersHash, activeScenario?.id, activeScenario?.title]);

  const debouncedScenarioId = useDebounce(activeScenario?.id, 300);
  const debouncedActiveScenarioParametersHash = useDebounce(activeScenarioParametersHash, 500);
  const debouncedTimeHorizon = useDebounce(timeHorizonYears, 300);

  // Removed attachRelativeSeries - backend handles all relative calculations

  // Track last analysis parameters to prevent duplicates
  // Track last analysis key per scenario to avoid duplicate runs
  const lastAnalysisMapRef = useRef<Record<string, string>>({});

  // Phase 6.1: Monitor analysis cache
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('[ANALYSIS_CACHE]', lastAnalysisMapRef.current);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Note: scenario visibility is now controlled at the individual scenario level

  // Helper to trigger analysis for a specific scenario
  const runAnalysisForScenario = useCallback((scenario: Scenario, timeYears: number) => {
    if (!scenario?.parameters?.property_details?.home_price || defaultsLoading) return;

    const paramsHash = getParametersHash(scenario);
    // Include baseline parameters in the analysis key so that when baseline changes,
    // all scenarios are re-analyzed even if their own parameters haven't changed
    const baselineHash = baselineScenario ? getParametersHash(baselineScenario) : '';
    const analysisKey = `${scenario.id}_${paramsHash}_${baselineHash}_${timeYears}`;
    const lastKey = lastAnalysisMapRef.current[scenario.id];
    
    const isDuplicate = lastKey === analysisKey;
    
    // Phase 1.2: Enhanced logging for analysis deduplication
    console.log('[ANALYSIS_KEY]', {
      scenarioId: scenario.id,
      analysisKey: analysisKey.substring(0, 100),
      lastKey: lastKey ? lastKey.substring(0, 100) : 'none',
      isDuplicate,
      paramsHashPreview: paramsHash.substring(0, 100),
      baselineHashPreview: baselineHash.substring(0, 100)
    });
    
    if (isDuplicate) {
      if (debugEnabled) dlog('[RunAnalysis] SKIPPING (duplicate)', scenario.title, 'key:', analysisKey.substring(0, 80) + '...');
      return;
    }
    
    if (debugEnabled) dlog('[RunAnalysis] RUNNING', {
      scenarioType: scenario.scenarioType,
      title: scenario.title,
      scenarioId: scenario.id,
      analysisKey: analysisKey.substring(0, 80) + '...',
      reason: lastKey ? 'hash changed' : 'first run'
    });

    // Store scenario ID at the time of analysis start
    lastAnalysisMapRef.current[scenario.id] = analysisKey;
    setScenarioAnalyzing(scenario.id, true);

    const optimizedParams = createOptimizedParams(scenario, timeYears);
    if (debugEnabled) dlog('[OptimizedParams] Sending to API:', scenario.title, {
      scenarioType: (optimizedParams as any).scenario_type,
      rentParam: optimizedParams.renting_params?.current_rent_living_alone,
      homePrice: optimizedParams.property_details?.home_price,
      investmentReturn: optimizedParams.market_assumptions?.investment_return_rate,
      marketAssumptions: optimizedParams.market_assumptions
    });
    if (debugEnabled) {
      const subset = {
        scenario_type: (optimizedParams as any).scenario_type,
        time_horizon_years: optimizedParams.analysis_settings?.time_horizon_years,
        market_assumptions: optimizedParams.market_assumptions,
        renting_params: {
          current_rent_with_roommates: optimizedParams.renting_params?.current_rent_with_roommates,
          current_rent_living_alone: optimizedParams.renting_params?.current_rent_living_alone,
          rental_strategy: optimizedParams.renting_params?.rental_strategy,
        },
      };
      dlog('[OptimizedParams]', safeJson(subset));
    }
    
    // Find baseline scenario
    if (baselineScenario && baselineScenario.id !== scenario.id) {
      // Use baseline analysis
      const baselineParams = createOptimizedParams(baselineScenario, timeYears);
      if (debugEnabled) dlog('Using baseline analysis with baseline:', baselineScenario.title);
      
      baselineAnalysisMutation.mutate({
        scenarioParams: optimizedParams,
        baselineParams: baselineParams
      }, {
        onSuccess: (result) => {
          // Phase 5.1: Log analysis completion to check for race conditions
          const scenarioIdAtRequest = scenario.id;
          console.log('[ANALYSIS_COMPLETE]', {
            requestedFor: scenarioIdAtRequest,
            currentActiveId: activeScenario?.id,
            mismatch: scenarioIdAtRequest !== activeScenario?.id,
            type: 'baseline'
          });
          
          if (debugEnabled) dlog(`Baseline analysis completed successfully for scenario ${scenario.id}`);
          if (debugEnabled) {
            const yearly: any = result?.yearly_data || {};
            dlog('[AnalysisResult:baseline]', {
              scenarioId: scenario.id,
              baselineId: baselineScenario.id,
              rent: (optimizedParams as any).renting_params?.current_rent_living_alone,
              finalDifference: yearly?.relative_net_worth?.slice(-1)[0],
              rentingSeriesLength: yearly?.renting_net_worth?.length,
              baselineSeriesLength: yearly?.baseline_net_worth?.length,
            });
          }
          if (debugEnabled) {
            const yd: any = result?.yearly_data;
            dlog('[AnalysisResult] Storing result for', scenario.title, {
              finalRentingNW: yd?.renting_net_worth?.[yd?.renting_net_worth?.length - 1],
              resultHasData: !!yd,
            });
          }
          updateScenarioResult(scenario.id, result);
        },
        onError: (error) => {
          console.error(`Baseline analysis failed for scenario ${scenario.id}:`, error);
          delete lastAnalysisMapRef.current[scenario.id];
          const anyErr: any = error;
          const detail = anyErr?.response?.data?.detail;
          let errorMessage: string | undefined;
          if (Array.isArray(detail)) {
            errorMessage = detail.map((d: any) => d?.msg || d?.type).filter(Boolean).join('; ');
          } else if (typeof detail === 'string') {
            errorMessage = detail;
          } else {
            errorMessage = anyErr?.message;
          }
          if (!errorMessage) errorMessage = 'Analysis failed. Please check your parameters.';
          updateScenarioResult(scenario.id, undefined, errorMessage);
        },
      });
    } else {
      // Use standard analysis (for baseline scenario itself or when no baseline)
      analysisMutation.mutate(optimizedParams, {
        onSuccess: (result) => {
          // Phase 5.1: Log analysis completion to check for race conditions
          const scenarioIdAtRequest = scenario.id;
          console.log('[ANALYSIS_COMPLETE]', {
            requestedFor: scenarioIdAtRequest,
            currentActiveId: activeScenario?.id,
            mismatch: scenarioIdAtRequest !== activeScenario?.id,
            type: 'standard'
          });
          
          if (debugEnabled) dlog(`Analysis completed successfully for scenario ${scenario.id}`);
          if (debugEnabled) {
            const yearly: any = result?.yearly_data || {};
            dlog('[AnalysisResult:standard]', {
              scenarioId: scenario.id,
              scenarioType: scenario.scenarioType,
              rent: (optimizedParams as any).renting_params?.current_rent_living_alone,
              finalBuying: yearly?.buying_net_worth?.slice(-1)[0],
              finalRenting: yearly?.renting_net_worth?.slice(-1)[0],
              difference: result?.net_worth_difference,
            });
          }
          updateScenarioResult(scenario.id, result);
        },
        onError: (error) => {
          console.error(`Analysis failed for scenario ${scenario.id}:`, error);
          delete lastAnalysisMapRef.current[scenario.id];
          const anyErr: any = error;
          const detail = anyErr?.response?.data?.detail;
          let errorMessage: string | undefined;
          if (Array.isArray(detail)) {
            errorMessage = detail.map((d: any) => d?.msg || d?.type).filter(Boolean).join('; ');
          } else if (typeof detail === 'string') {
            errorMessage = detail;
          } else {
            errorMessage = anyErr?.message;
          }
          if (!errorMessage) errorMessage = 'Analysis failed. Please check your parameters.';
          updateScenarioResult(scenario.id, undefined, errorMessage);
        },
      });
    }
  }, [defaultsLoading, analysisMutation, baselineAnalysisMutation, setScenarioAnalyzing, updateScenarioResult, baselineScenario]);

  // Run analysis for the active scenario when its params change (NOT when switching scenarios)
  useEffect(() => {
    if (!debouncedScenarioId) return;
    const currentScenario = scenarios.find(s => s.id === debouncedScenarioId);
    if (currentScenario) {
      if (debugEnabled) dlog('[Effect:ActiveScenario] Parameter change detected for', currentScenario.title);
      runAnalysisForScenario(currentScenario, debouncedTimeHorizon);
    }
  }, [debouncedActiveScenarioParametersHash, debouncedTimeHorizon, scenarios, runAnalysisForScenario]);
  // NOTE: Removed debouncedScenarioId from dependencies - scenario selection alone shouldn't trigger analysis

  // Re-analyze all scenarios when time horizon changes
  // NOTE: This might be redundant since time horizon changes should trigger parameter hash changes
  // Keeping for now but consider removing if parameter hash changes handle this
  useEffect(() => {
    scenarios.forEach((scenario) => {
      runAnalysisForScenario(scenario, debouncedTimeHorizon);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTimeHorizon, scenarios]);

  // Re-analyze all scenarios when baseline changes or baseline parameters change
  useEffect(() => {
    const baselineScenario = scenarios.find(s => s.isBaseline);
    if (baselineScenario) {
      if (debugEnabled) console.log(`Baseline changed to: ${baselineScenario.title}, re-analyzing all scenarios`);
      scenarios.forEach((scenario) => {
        runAnalysisForScenario(scenario, debouncedTimeHorizon);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scenarios.map(s => s.isBaseline).join(','),
    // Watch for baseline parameter changes - use a more robust approach
    scenarios.map(s => s.isBaseline ? getParametersHash(s) : '').join('|')
  ]);

  // Ref to track scenarios being loaded with their IDs
  const loadingQueueRef = useRef<Array<{id: string, title: string, isBaseline: boolean, link?: string}>>([]);
  const loadingInProgressRef = useRef(false);
  const lastProcessedCountRef = useRef(0);
  
  // Effect to update scenario titles and baseline status as they're created
  useEffect(() => {
    if (!loadingInProgressRef.current) return;
    
    const currentCount = scenarios.length;
    if (debugEnabled) dlog(`App: Scenario count changed to ${currentCount}, last processed: ${lastProcessedCountRef.current}`);
    
    // Process any new scenarios that were added since last check
    if (currentCount > lastProcessedCountRef.current) {
      const newScenariosCount = currentCount - lastProcessedCountRef.current;
      if (debugEnabled) dlog(`App: Processing ${newScenariosCount} new scenario(s)`);
      
      for (let i = 0; i < newScenariosCount; i++) {
        const scenarioIndex = lastProcessedCountRef.current + i;
        const scenario = scenarios[scenarioIndex];
        const queueItem = loadingQueueRef.current[scenarioIndex];
        
        if (scenario && queueItem) {
          if (debugEnabled) dlog(`App: Updating scenario ${scenario.id} (index ${scenarioIndex}) title to "${queueItem.title}"`);
          updateScenarioTitle(scenario.id, queueItem.title);
          updateScenarioLink(scenario.id, queueItem.link);
          
          if (queueItem.isBaseline && !scenario.isBaseline) {
            if (debugEnabled) dlog(`App: Setting scenario ${scenario.id} as baseline`);
            toggleBaseline(scenario.id);
          }
        }
      }
      
      lastProcessedCountRef.current = currentCount;
      
      // If we've processed all expected scenarios, we're done
      if (currentCount >= loadingQueueRef.current.length) {
        if (debugEnabled) dlog('App: Finished loading all scenarios');
        loadingInProgressRef.current = false;
        lastProcessedCountRef.current = 0;
      }
    }
  }, [scenarios, updateScenarioTitle, updateScenarioLink, toggleBaseline]);

  // Mark analysis as unsaved when scenarios or time horizon change (skip when loading saved analysis)
  useEffect(() => {
    if (loadingInProgressRef.current) return;
    // Only mark unsaved if there is at least some content
    if (scenarios.length > 0) {
      setIsSaved(false);
    }
  }, [scenarios, timeHorizonYears]);


  // Show checkout success page if redirected from checkout
  if (isCheckoutSuccess) {
    return <CheckoutSuccess />;
  }

  // Show profile page if on /profile route
  if (isProfilePage) {
    return <ProfilePage />;
  }

  // Show legal pages
  if (isPrivacyPage) {
    return <PrivacyPolicy />;
  }
  if (isTermsPage) {
    return <TermsOfService />;
  }

  if (defaultsLoading) {
    return (
      <MainLayout onOpenTutorial={handleOpenTutorialModal}>
        <div className="col-span-12 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading default parameters...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Get current analysis result for saving
  const currentAnalysisResult = activeScenario?.analysisResult;
  
  // Handle loading a saved analysis
  const handleLoadAnalysis = (analysis: any) => {
    if (debugEnabled) {
      dlog('App: handleLoadAnalysis called with:', analysis);
      dlog('App: analysis.scenarios:', analysis?.scenarios);
    }
    
    // Load analysis title if present
    if (typeof analysis?.title === 'string') {
      if (debugEnabled) dlog('App: Setting analysis title to:', analysis.title);
      setAnalysisTitle(analysis.title);
    }
    
    // Load time horizon if present
    if (typeof analysis?.time_horizon_years === 'number') {
      if (debugEnabled) dlog('App: Setting time horizon to:', analysis.time_horizon_years);
      setTimeHorizonYears(analysis.time_horizon_years);
    }
    
    // Load scenarios
    if (analysis?.scenarios && Array.isArray(analysis.scenarios)) {
      if (debugEnabled) dlog('App: Loading', analysis.scenarios.length, 'scenarios');
      
      // Clear existing scenarios by removing them one by one
      scenarios.forEach(scenario => {
        removeScenario(scenario.id);
      });
      
      // Set up the loading queue with placeholders for IDs
      loadingQueueRef.current = analysis.scenarios.map((s: any, index: number) => ({
        id: `loading-${index}`, // Placeholder, will be replaced with actual scenario ID
        title: s.title,
        isBaseline: s.isBaseline || false,
        link: typeof s.link === 'string' ? s.link : undefined
      }));
      loadingInProgressRef.current = true;
      lastProcessedCountRef.current = 0;
      
      if (debugEnabled) dlog('App: Loading queue set up:', loadingQueueRef.current.map(q => q.title));
      
      // Load scenarios sequentially with a slight delay between each
      analysis.scenarios.forEach((savedScenario: any, index: number) => {
        setTimeout(() => {
          if (debugEnabled) dlog(`App: Creating scenario ${index + 1}: "${savedScenario.title}"`);
          addScenario(savedScenario.scenarioType, savedScenario.parameters);
        }, index * 400);
      });
      // Mark as saved for this loaded analysis
      setIsSaved(true);
    } else if (analysis?.parameters) {
      // Legacy support: single parameters object
      if (debugEnabled) dlog('App: Loading legacy single-scenario analysis');
      
      if (activeScenario) {
        updateScenarioParameters(activeScenario.id, analysis.parameters as CalculatorParams);
      } else if (scenarios.length > 0) {
        const firstScenario = scenarios[0];
        updateScenarioParameters(firstScenario.id, analysis.parameters as CalculatorParams);
        setActiveScenarioId(firstScenario.id);
      } else {
        const scenarioType = analysis.parameters.scenario_type || 'buying';
        addScenario(scenarioType, analysis.parameters as CalculatorParams);
      }
    }
  };

  return (
    <>
    <MainLayout
      currentParameters={activeScenario?.parameters}
      currentAnalysisResult={currentAnalysisResult}
      onLoadAnalysis={handleLoadAnalysis}
      onOpenTutorial={handleOpenTutorialModal}
    >
      {/* Left Column - Analysis Period and Scenarios */}
      <div className="col-span-4 flex flex-col" style={{ height: '635px' }}>
        {/* Save/Load + Analysis Title */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 shadow-md mb-6">
          {/* Save / Saved Analyses controls */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <SaveButton onSave={() => setIsSaveModalOpen(true)} disabled={!activeScenario?.parameters} />
            </div>
            <button
              className="ml-3 flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors duration-200"
              onClick={() => setIsSavedAnalysesOpen(true)}
              title="View Your Analyses"
            >
              <span className="text-sm font-medium">Your analyses</span>
            </button>
          </div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Analysis Title</label>
          <input
            type="text"
            value={analysisTitle}
            onChange={(e) => { setAnalysisTitle(e.target.value); setIsSaved(false); }}
            placeholder="e.g., Starter Home vs Rent"
            className="w-full px-3 py-2 mb-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Analysis Period */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 shadow-md mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Analysis Duration: {timeHorizonYears} years</label>
          <SliderInput
            config={analysisPeriodConfig}
            value={timeHorizonYears}
            onChange={setTimeHorizonYears}
          />
        </div>

        {/* Baseline warning */}
        {scenarios.length > 0 && !scenarios.find(s => s.isBaseline) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  No Baseline Selected
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Please set one scenario as the baseline to enable relative comparisons.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scenarios container - flex to fill remaining space and align with chart */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 shadow-md p-4 flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">Scenarios</h2>
            <AddScenarioButton 
              onAdd={handleAddScenario} 
              disabled={hasPremiumSubscription ? !canAddScenario : false} 
              variant="compact" 
              shouldPulse={scenarios.length < 2}
            />
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            <ScenarioCarousel
              scenarios={scenarios}
              activeScenarioId={activeScenarioId}
              onSelectScenario={setActiveScenarioId}
              onAddScenario={handleAddScenario}
              onRemoveScenario={removeScenario}
              onUpdateScenarioTitle={updateScenarioTitle}
              onUpdateScenarioLink={updateScenarioLink}
              onToggleVisibility={toggleScenarioVisibility}
              onToggleBaseline={toggleBaseline}
              canAddScenario={canAddScenario}
              globalOrder={scenarios.map(s => s.id)}
            />
          </div>
        </div>
      </div>

      {/* Right Column - Chart Only */}
      <div className="col-span-8 space-y-6">
        {/* Main Chart */}
        <NetWorthChart
          scenarios={scenarios}
          timeHorizonYears={timeHorizonYears}
        />
      </div>

      {/* Full Width Scenario Parameters - below the grid */}
      {activeScenario && (
        <div className="col-span-12 mt-6" ref={parameterControlsRef}>
          <ParameterControls
            scenarioType={activeScenario.scenarioType}
            timeHorizonYears={timeHorizonYears}
            onTimeHorizonChange={setTimeHorizonYears}
            parameters={activeScenario.parameters}
            onParametersChange={(params) => {
              const changes: any = {};
              if (activeScenario.scenarioType === 'renting') {
                if (activeScenario.parameters?.renting_params?.current_rent_living_alone !== params?.renting_params?.current_rent_living_alone) {
                  changes.rent = {
                    from: activeScenario.parameters?.renting_params?.current_rent_living_alone,
                    to: params?.renting_params?.current_rent_living_alone
                  };
                }
                if (activeScenario.parameters?.market_assumptions?.investment_return_rate !== params?.market_assumptions?.investment_return_rate) {
                  changes.investmentReturn = {
                    from: activeScenario.parameters?.market_assumptions?.investment_return_rate,
                    to: params?.market_assumptions?.investment_return_rate
                  };
                  if (debugEnabled) {
                    dlog('[App] Investment return rate changed:', {
                      from: activeScenario.parameters?.market_assumptions?.investment_return_rate,
                      to: params?.market_assumptions?.investment_return_rate,
                      scenarioId: activeScenario.id,
                      scenarioType: activeScenario.scenarioType
                    });
                  }
                }
              } else {
                if (activeScenario.parameters?.property_details?.home_price !== params?.property_details?.home_price) {
                  changes.homePrice = {
                    from: activeScenario.parameters?.property_details?.home_price,
                    to: params?.property_details?.home_price
                  };
                }
              }

              if (debugEnabled && Object.keys(changes).length > 0) {
                dlog('[App] Parameters CHANGED:', activeScenario.title, changes);
              }
              if (debugEnabled) {
                dlog('[App] Dispatching parameter update', {
                  scenarioId: activeScenario.id,
                  scenarioType: activeScenario.scenarioType,
                  isBaseline: activeScenario.isBaseline,
                  affectedKeys: Object.keys(changes),
                  rentingParams: params?.renting_params,
                  marketAssumptions: params?.market_assumptions,
                  timestamp: Date.now(),
                });
              }
              updateScenarioParameters(activeScenario.id, params);
            }}
            isAnalyzing={activeScenario.isAnalyzing}
            scenarioTitle={activeScenario.title}
            shouldPulse={shouldPulseParameterControls}
          />
        </div>
      )}
    </MainLayout>

    <TutorialModal
      isOpen={tutorialModalState.isOpen}
      onClose={handleCloseTutorialModal}
      defaultTab={tutorialModalState.defaultTab}
    />

    <UpgradePrompt
      isOpen={isScenarioUpgradePromptOpen}
      onClose={() => setIsScenarioUpgradePromptOpen(false)}
      requiresAuth={!user}
    />

    {/* Save Analysis Modal */}
    {scenarios.length > 0 && (
      <SaveAnalysisModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSaved={() => {
          if (debugEnabled) dlog('Analysis saved successfully');
          setIsSaved(true);
        }}
        scenarios={scenarios}
        timeHorizonYears={timeHorizonYears}
        initialTitle={analysisTitle}
      />
    )}

    {/* Saved Analyses List */}
    <SavedAnalysesList
      isOpen={isSavedAnalysesOpen}
      onClose={() => setIsSavedAnalysesOpen(false)}
      onLoadAnalysis={handleLoadAnalysis}
    />
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
